import { z } from 'zod';
import { SupportTypes } from './enums';
import { optionalChoice, optionalDate, optionalMoney, versionField } from './fields';
import { idSchema } from './ids';

/**
 * Support records (FR-SUP-001…009): one per customer product — whether support is in force, from
 * when, which type and the monthly fee (finance data, ADR 0004), and whether it needs review.
 */
const required = (message: string) => z.string().min(1, { message }).pipe(idSchema);

export const supportInputSchema = z
  .object({
    customerId: required('Klients ir obligāts!'),
    productId: required('Produkts ir obligāts!'),
    isActive: z.boolean(),
    needsReview: z.boolean(),
    startDate: optionalDate,
    endDate: optionalDate,
    supportType: optionalChoice(SupportTypes.schema),
    monthlyFee: optionalMoney,
  })
  .superRefine((v, ctx) => {
    if (v.isActive && !v.startDate)
      ctx.addIssue({
        code: 'custom',
        path: ['startDate'],
        message: 'Sākuma datums ir obligāts, ja atbalsts ir spēkā!',
      });
  })
  // Support not in force has no dates, type or fee (FR-SUP-004).
  .transform((v) =>
    v.isActive ? v : { ...v, startDate: null, endDate: null, supportType: null, monthlyFee: null },
  );
export type SupportInput = z.input<typeof supportInputSchema>;

export const supportUpdateSchema = supportInputSchema.and(z.object(versionField));

export const supportSchema = z.object({
  id: idSchema,
  customerId: idSchema,
  customerName: z.string(),
  productId: idSchema,
  productName: z.string(),
  isActive: z.boolean(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  supportType: SupportTypes.schema.nullable(),
  /** `null` also when the reader may not see finance data. */
  monthlyFee: z.string().nullable(),
  needsReview: z.boolean(),
  version: z.number().int(),
});
export type Support = z.infer<typeof supportSchema>;

export const supportListQuerySchema = z.object({ customerId: idSchema.optional() });

/** "⚠️ Klienti bez produktiem" and "⚠️ Produkti bez atbalsta informācijas" (FR-SUP-008/009). */
export const supportGapsSchema = z.object({
  customersWithoutProducts: z.array(z.object({ id: idSchema, name: z.string() })),
  productsWithoutSupport: z.array(
    z.object({
      customerId: idSchema,
      customerName: z.string(),
      productId: idSchema,
      productName: z.string(),
    }),
  ),
});
export type SupportGaps = z.infer<typeof supportGapsSchema>;
